import { create } from 'zustand'
import {
  WAVE_FIXTURES,
  WAVE_CALENDARS,
  REFERENCE_WEEKS,
} from '@/components/WavePlanning/data'
import { validateWavePercent } from '@/components/WavePlanning/model'
import { SKU_WAVE_FIXTURES } from '@/components/WavePlanning/sku-data'
import type {
  SkuWaveComment,
  SkuWaveDrafts,
} from '@/components/WavePlanning/sku-model'
import {
  demoDistributionCenters,
  equalDcSplit,
  type DcDrafts,
  type DcSplit,
} from '@/components/WavePlanning/dc-model'

interface WavePlanningState {
  drafts: Record<string, number[]>
  skuDrafts: Record<string, SkuWaveDrafts>
  skuComments: Record<string, Record<string, SkuWaveComment[]>>
  dcDrafts: Record<string, DcDrafts>
  setDcSplit: (
    calendar: string,
    subclass: string,
    lineId: string,
    week: number,
    split: DcSplit
  ) => void
  setWaveDcPercent: (
    calendar: string,
    subclass: string,
    lineIds: string[],
    week: number,
    dcId: string,
    percent: number
  ) => void
  resetDcChanges: (calendar: string, subclass: string) => void
  updateWave: (
    calendar: string,
    subclass: string,
    index: number,
    percent: string
  ) => void
  reset: (calendar: string, subclass: string) => void
  updateSkuWave: (
    calendar: string,
    subclass: string,
    lineId: string,
    index: number,
    percent: string
  ) => void
  resetSkuChanges: (calendar: string, subclass: string, lineId?: string) => void
  addSkuComment: (
    calendar: string,
    subclass: string,
    lineId: string,
    content: string,
    authorId: string,
    authorName: string
  ) => void
  deleteSkuComment: (
    calendar: string,
    subclass: string,
    lineId: string,
    commentId: string,
    authorId: string
  ) => void
}

function validLine(calendar: string, subclass: string, lineId: string) {
  return (
    WAVE_CALENDARS.some(item => item.id === calendar) &&
    SKU_WAVE_FIXTURES.some(
      item =>
        item.id === subclass && item.lines.some(line => line.id === lineId)
    )
  )
}

// Intentionally memory-only: navigation retains edits; reloading restores fixtures.
export const useWavePlanningStore = create<WavePlanningState>((set, get) => ({
  drafts: {},
  skuDrafts: {},
  skuComments: {},
  dcDrafts: {},
  setDcSplit: (calendar, subclass, lineId, week, split) => {
    if (
      !validLine(calendar, subclass, lineId) ||
      !REFERENCE_WEEKS.some(
        item => item.week === week && item.event !== 'NO WAVE'
      ) ||
      Object.values(split).some(
        value => !Number.isFinite(value) || validateWavePercent(String(value))
      )
    )
      return
    const key = `${calendar}:${subclass}`
    set(state => ({
      dcDrafts: {
        ...state.dcDrafts,
        [key]: {
          ...state.dcDrafts[key],
          [lineId]: { ...state.dcDrafts[key]?.[lineId], [week]: { ...split } },
        },
      },
    }))
  },
  setWaveDcPercent: (calendar, subclass, lineIds, week, dcId, percent) => {
    const fixture = SKU_WAVE_FIXTURES.find(item => item.id === subclass)
    if (
      !fixture ||
      !lineIds.length ||
      !WAVE_CALENDARS.some(item => item.id === calendar) ||
      lineIds.some(id => !fixture.lines.some(line => line.id === id)) ||
      !REFERENCE_WEEKS.some(
        item => item.week === week && item.event !== 'NO WAVE'
      ) ||
      !Number.isFinite(percent) ||
      validateWavePercent(String(percent))
    )
      return
    const centers = demoDistributionCenters(fixture.dcCount)
    if (!centers.some(dc => dc.id === dcId)) return
    const fallback = equalDcSplit(centers)
    const key = `${calendar}:${subclass}`
    set(state => {
      const scenario = { ...state.dcDrafts[key] }
      lineIds.forEach(id => {
        const previous = scenario[id]?.[week] ?? fallback
        scenario[id] = {
          ...scenario[id],
          [week]: { ...previous, [dcId]: percent },
        }
      })
      return { dcDrafts: { ...state.dcDrafts, [key]: scenario } }
    })
  },
  resetDcChanges: (calendar, subclass) => {
    const key = `${calendar}:${subclass}`
    set(state => {
      const dcDrafts = { ...state.dcDrafts }
      delete dcDrafts[key]
      return { dcDrafts }
    })
  },
  updateWave: (calendar, subclass, index, percent) => {
    const fixture = WAVE_FIXTURES.find(item => item.id === subclass)
    if (
      !fixture ||
      !WAVE_CALENDARS.some(item => item.id === calendar) ||
      validateWavePercent(percent) ||
      !REFERENCE_WEEKS[index] ||
      REFERENCE_WEEKS[index].event === 'NO WAVE'
    )
      return
    const { drafts } = get()
    const key = `${calendar}:${subclass}`
    const values = [...(drafts[key] ?? fixture.plannedWaves)]
    values[index] = Number(percent.trim()) / 100
    set({ drafts: { ...drafts, [key]: values } })
  },
  reset: (calendar, subclass) => {
    const { drafts } = get()
    const next = { ...drafts }
    delete next[`${calendar}:${subclass}`]
    set({ drafts: next })
  },
  updateSkuWave: (calendar, subclass, lineId, index, percent) => {
    if (
      !validLine(calendar, subclass, lineId) ||
      validateWavePercent(percent) ||
      !REFERENCE_WEEKS[index] ||
      REFERENCE_WEEKS[index].event === 'NO WAVE'
    )
      return
    const { drafts, skuDrafts } = get()
    const key = `${calendar}:${subclass}`
    const overall =
      drafts[key] ??
      WAVE_FIXTURES.find(item => item.id === subclass)!.plannedWaves
    const previous = skuDrafts[key]?.[lineId] ?? overall
    const value = Number(percent.trim()) / 100
    if (previous[index] === value) return
    // Snapshot the whole calendar on the first edit. New overview waves remain zero here.
    const values = [...previous]
    values[index] = value
    set({
      skuDrafts: {
        ...skuDrafts,
        [key]: { ...skuDrafts[key], [lineId]: values },
      },
    })
  },
  resetSkuChanges: (calendar, subclass, lineId) => {
    const { skuDrafts } = get()
    const key = `${calendar}:${subclass}`
    const next = { ...skuDrafts }
    if (lineId) {
      next[key] = { ...next[key] }
      delete next[key][lineId]
      if (!Object.keys(next[key]).length) delete next[key]
    } else delete next[key]
    set({ skuDrafts: next })
  },
  addSkuComment: (
    calendar,
    subclass,
    lineId,
    content,
    authorId,
    authorName
  ) => {
    if (
      !validLine(calendar, subclass, lineId) ||
      !content.trim() ||
      content.length > 2000
    )
      return
    const { skuComments } = get()
    const key = `${calendar}:${subclass}`
    const comments = skuComments[key]?.[lineId] ?? []
    const comment: SkuWaveComment = {
      id: crypto.randomUUID(),
      content: content.trim(),
      authorId,
      authorName,
      createdAt: new Date().toISOString(),
    }
    set({
      skuComments: {
        ...skuComments,
        [key]: { ...skuComments[key], [lineId]: [...comments, comment] },
      },
    })
  },
  deleteSkuComment: (calendar, subclass, lineId, commentId, authorId) => {
    const { skuComments } = get()
    const key = `${calendar}:${subclass}`
    const comments = (skuComments[key]?.[lineId] ?? []).filter(
      comment => comment.id !== commentId || comment.authorId !== authorId
    )
    set({
      skuComments: {
        ...skuComments,
        [key]: { ...skuComments[key], [lineId]: comments },
      },
    })
  },
}))
